/**
 * CohortEventDetailDrawer Component (MED-642)
 * Full session detail slide-out for cohort events with two tabs:
 * - Session: Coach attendance (card-style) + Resources accordion
 * - Internal Notes: Medley staff only notes
 * Matches group coaching SessionDetailDrawer layout and patterns.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Loader,
  Center,
  Alert,
  Box,
  ActionIcon,
  ScrollArea,
  Tabs,
  Avatar,
  Paper,
  Collapse,
  UnstyledButton,
  Divider,
  Select,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  AlertCircle,
  X,
  Check,
  Lock,
  FileText,
  File,
  Download,
  Plus,
  ChevronDown,
  ChevronUp,
  Minus,
} from 'react-feather';
import {
  cohortEventsApi,
  type CohortEventAttendanceInput,
  type CohortEventAttendanceStatus,
  type CohortEventRosterEntry,
  type CohortEventRosterRole,
  type CohortEventWithDetails,
  type CohortEventNoteWithAuthor,
  type CohortEventResourceWithDetails,
} from '../../../../services/api/cohortEvents';
import { getApiErrorMessage } from '../../../../services/api/client';
import type { AbsentReason, ResourceCategory } from '../../../../types';
import { formatDateTime, getZoneCode } from '../../../../utils/formatters';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { RichTextContent } from '../../../../components/common/RichTextEditor/RichTextContent';
import { AddResourceModal } from '../AddResourceModal';
import styles from './SessionDetailModal.module.css';
import {
  absenceReasonText,
  clearedUserIds,
  isBulkMarkable,
  isOptionalCoach,
  nextStatus,
} from './cohortEventRoster';

// ── Types ────────────────────────────────────────────────────────────────────

interface AttendanceRow extends CohortEventRosterEntry {
  /** The saved status when the drawer loaded. Null when there was none. */
  loaded_status: CohortEventAttendanceStatus | null;
  /** Null until the CM picks a status. */
  selected_status: CohortEventAttendanceStatus | null;
  selected_reason: AbsentReason | null;
  notes: string;
}

const ABSENT_REASON_OPTIONS: { value: AbsentReason; label: string }[] = [
  { value: 'business_conflict', label: 'Business Conflict' },
  { value: 'personal_conflict', label: 'Personal Conflict' },
  { value: 'no_notice', label: 'No Notice' },
];

const STATUS_BUTTONS: {
  value: CohortEventAttendanceStatus;
  label: string;
  color: string;
  Icon: typeof Check;
}[] = [
  { value: 'attended', label: 'Attended', color: 'medleyGreen', Icon: Check },
  { value: 'partial', label: 'Partial', color: 'yellow', Icon: Minus },
  { value: 'absent', label: 'Absent', color: 'red', Icon: X },
];

interface CohortEventDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  event: CohortEventWithDetails | null;
  onComplete?: () => void;
  categories?: ResourceCategory[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatHeaderDateRange(
  startStr: string | null,
  endStr: string | null,
  tz: string | null,
): string {
  if (!startStr) return 'No date set';
  try {
    const tzOpt = tz || 'America/New_York';
    const start = new Date(startStr);
    const datePart = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: tzOpt,
    }).format(start);
    const startTime = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tzOpt,
    }).format(start);

    if (endStr) {
      const end = new Date(endStr);
      const endTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: tzOpt,
      }).format(end);
      return `${datePart} \u00B7 ${startTime} - ${endTime} ${getZoneCode(tzOpt, end)}`;
    }

    return `${datePart} \u00B7 ${startTime} ${getZoneCode(tzOpt, start)}`;
  } catch {
    return startStr;
  }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A saved status the drawer can show. 'present' is a legacy value for attended. */
function toSelectedStatus(current: string): CohortEventAttendanceStatus | null {
  if (current === 'attended' || current === 'present') return 'attended';
  if (current === 'partial' || current === 'absent') return current;
  return null;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatShortDate(iso: string | null): string {
  if (!iso) return 'No date set';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

// ── Roster section (MED-1349) ────────────────────────────────────────────────

interface RosterSectionProps {
  title: string;
  rows: AttendanceRow[];
  loading: boolean;
  editable: boolean;
  showPayment: boolean;
  eventStart: string | null;
  onStatus: (userId: string, status: CohortEventAttendanceStatus) => void;
  onReason: (userId: string, reason: AbsentReason | null) => void;
  onMarkAllAttended: () => void;
}

function RosterSection({
  title,
  rows,
  loading,
  editable,
  showPayment,
  eventStart,
  onStatus,
  onReason,
  onMarkAllAttended,
}: RosterSectionProps) {
  const canMarkAll = editable && rows.some(isBulkMarkable);

  return (
    <div>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
          {title}
        </Text>
        {canMarkAll && (
          <Button variant="subtle" size="xs" onClick={onMarkAllAttended}>
            Mark all attended
          </Button>
        )}
      </Group>

      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No attendees found for this event
        </Text>
      ) : (
        <Stack gap="xs">
          {rows.map((row) => (
            <Paper
              key={row.user_id}
              withBorder
              p="sm"
              radius="md"
              style={{
                backgroundColor:
                  row.selected_status === 'attended' ? 'rgba(74, 124, 89, 0.04)' : undefined,
              }}
            >
              <Group justify="space-between" wrap="wrap" gap={8} align="flex-start">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 180px' }}>
                  <Avatar size={32} radius="xl" color="medleyGreen" variant="filled">
                    {row.name
                      ? getInitials(row.name)
                      : (row.email ?? '?').substring(0, 2).toUpperCase()}
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    {/* MED-1349 QA round 2: the full name wraps; it is never cut. */}
                    <Text size="sm" fw={600} style={{ overflowWrap: 'anywhere' }}>
                      {row.name || row.email}
                    </Text>
                    {/* MED-1349: same label as the coach attendance list, on its own line. */}
                    {row.is_withdrawn && (
                      <Badge
                        size="xs"
                        variant="filled"
                        mt={2}
                        styles={{
                          root: {
                            backgroundColor: '#E8E8E8',
                            color: '#444444',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            fontWeight: 600,
                          },
                        }}
                      >
                        Withdrawn
                      </Badge>
                    )}
                    {/* MED-1349 QA round 3: invited-only coaches need no status. */}
                    {isOptionalCoach(row) && (
                      <Badge
                        size="xs"
                        variant="filled"
                        mt={2}
                        styles={{
                          root: {
                            backgroundColor: '#E8E8E8',
                            color: '#444444',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            fontWeight: 600,
                          },
                        }}
                      >
                        Optional
                      </Badge>
                    )}
                    {row.email === null && (
                      <Text size="xs" c="dimmed">
                        No Medley email, attendance cannot be recorded
                      </Text>
                    )}
                    {showPayment &&
                      row.pay_amount_cents !== null &&
                      (row.selected_status === 'absent' ? (
                        <Text size="xs" c="dimmed">
                          No payment. Marked absent.
                        </Text>
                      ) : row.selected_status === null && isOptionalCoach(row) ? (
                        <Text size="xs" c="dimmed">
                          No payment unless marked
                        </Text>
                      ) : (
                        <Text size="xs" c="dimmed">
                          Payment: {formatCents(row.pay_amount_cents)} &middot; Net 10 from{' '}
                          {formatShortDate(eventStart)}
                        </Text>
                      ))}
                    {row.is_paid && (
                      <Text size="xs" c="orange.8">
                        Paid through Bill.com. A change here does not change the payment.
                      </Text>
                    )}
                  </div>
                </Group>

                {row.email !== null && (
                  <Group gap={8} wrap="nowrap">
                    {STATUS_BUTTONS.map(({ value, label, color, Icon }) => (
                      <Tooltip
                        key={value}
                        label={row.selected_status === value ? `${label}. Click to clear.` : label}
                      >
                        <ActionIcon
                          size={36}
                          radius="xl"
                          aria-label={`${label}: ${row.name || row.email}`}
                          aria-pressed={row.selected_status === value}
                          disabled={!editable}
                          variant={row.selected_status === value ? 'filled' : 'default'}
                          color={row.selected_status === value ? color : 'gray'}
                          onClick={() => onStatus(row.user_id, value)}
                        >
                          <Icon size={16} />
                        </ActionIcon>
                      </Tooltip>
                    ))}
                  </Group>
                )}
              </Group>

              {row.selected_status === 'absent' && (
                <Select
                  mt="sm"
                  size="xs"
                  placeholder="Select reason"
                  aria-label="Absent reason"
                  data={ABSENT_REASON_OPTIONS}
                  value={row.selected_reason}
                  onChange={(value) => onReason(row.user_id, value as AbsentReason | null)}
                  error={row.selected_reason === null}
                  disabled={!editable}
                  allowDeselect={false}
                />
              )}
            </Paper>
          ))}
        </Stack>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function CohortEventDetailDrawer({
  opened,
  onClose,
  cohortId,
  event,
  onComplete,
  categories = [],
}: CohortEventDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<string | null>('session');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attendees, setAttendees] = useState<AttendanceRow[]>([]);
  const [includesParticipants, setIncludesParticipants] = useState(false);
  const [rosterLoadFailed, setRosterLoadFailed] = useState(false);
  const currentEventIdRef = useRef<string | null>(null);
  const isMobile = useMediaQuery('(max-width: 48em)');

  // Resources state
  const [resources, setResources] = useState<CohortEventResourceWithDetails[]>([]);
  const [resourcesExpanded, setResourcesExpanded] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [addResourceModalOpened, setAddResourceModalOpened] = useState(false);

  // Notes state
  const [notes, setNotes] = useState<CohortEventNoteWithAuthor[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [notesCount, setNotesCount] = useState(0);

  const eventId = event?.id ?? null;
  const attendanceMarked = event?.attendance_marked ?? false;

  // Determine if event is past and can have attendance marked
  const isEventPast = (() => {
    if (!event) return false;
    const endTime = event.scheduled_end || event.scheduled_start;
    if (!endTime) return false;
    return new Date(endTime) < new Date();
  })();
  const canMarkAttendance = isEventPast && !attendanceMarked;

  // MED-1349: the server accepts a save once the event has started.
  const hasStarted =
    !!event?.scheduled_start && new Date(event.scheduled_start) <= new Date();
  const coachRows = attendees.filter((a) => a.role === 'coach');
  const participantRows = attendees.filter((a) => a.role === 'participant');
  const missingCount = attendees.filter((a) => a.is_required && a.selected_status === null).length;
  const reasonText = absenceReasonText(attendees);
  const reasonMissing = reasonText !== null;
  const canSave =
    hasStarted && !loading && !rosterLoadFailed && missingCount === 0 && !reasonMissing;

  // ── Data loading ─────────────────────────────────────────────────────────

  const loadAttendees = useCallback(async () => {
    if (!eventId) return;
    // The drawer stays mounted between events. Clear the last event's rows
    // so a failed or late load can never save them to this event.
    currentEventIdRef.current = eventId;
    setAttendees([]);
    setIncludesParticipants(false);
    setRosterLoadFailed(false);
    setLoading(true);
    setError(null);
    try {
      const data = await cohortEventsApi.getAttendanceRoster(cohortId, eventId);
      if (currentEventIdRef.current !== eventId) return;
      setIncludesParticipants(data.includes_participants);
      setAttendees(
        data.attendees.map((a) => ({
          ...a,
          loaded_status: toSelectedStatus(a.current_status),
          selected_status: toSelectedStatus(a.current_status),
          selected_reason: a.current_absent_reason,
          notes: a.current_notes || '',
        })),
      );
    } catch {
      if (currentEventIdRef.current !== eventId) return;
      setRosterLoadFailed(true);
      setError('Failed to load attendee data');
    } finally {
      if (currentEventIdRef.current === eventId) setLoading(false);
    }
  }, [cohortId, eventId]);

  const loadResources = useCallback(async () => {
    if (!eventId) return;
    setResourcesLoading(true);
    try {
      const data = await cohortEventsApi.getResources(cohortId, eventId);
      setResources(data.data);
    } catch {
      // Silent fail
    } finally {
      setResourcesLoading(false);
    }
  }, [cohortId, eventId]);

  const loadNotes = useCallback(async () => {
    if (!eventId) return;
    setNotesLoading(true);
    try {
      const data = await cohortEventsApi.getNotes(cohortId, eventId);
      setNotes(data.notes);
      setNotesCount(data.notes.length);
    } catch {
      // Silent fail
    } finally {
      setNotesLoading(false);
    }
  }, [cohortId, eventId]);

  useEffect(() => {
    if (opened && eventId) {
      setActiveTab('session');
      loadAttendees();
      loadResources();
      loadNotes();
    }
  }, [opened, eventId, loadAttendees, loadResources, loadNotes]);

  // ── Attendance handlers ──────────────────────────────────────────────────

  // MED-1349 QA round 2: a click on the selected status clears it. The save
  // then leaves the row out, and the server removes a saved row that is not
  // required.
  const setStatus = (userId: string, clicked: CohortEventAttendanceStatus) => {
    setAttendees((prev) =>
      prev.map((a) => {
        if (a.user_id !== userId) return a;
        const status = nextStatus(a.selected_status, clicked);
        return {
          ...a,
          selected_status: status,
          selected_reason: status === 'absent' ? a.selected_reason : null,
        };
      }),
    );
  };

  const setReason = (userId: string, reason: AbsentReason | null) => {
    setAttendees((prev) =>
      prev.map((a) => (a.user_id === userId ? { ...a, selected_reason: reason } : a)),
    );
  };

  // Sets only the rows that have no status, so a saved absence stays.
  // Withdrawn participants are not required, so they stay as they are.
  const markAllAttended = (role: CohortEventRosterRole) => {
    setAttendees((prev) =>
      prev.map((a) =>
        a.role === role && isBulkMarkable(a)
          ? { ...a, selected_status: 'attended', selected_reason: null }
          : a,
      ),
    );
  };

  const handleSaveAttendance = async () => {
    if (!eventId || !canSave) return;
    setSaving(true);
    setError(null);
    try {
      const attendance: CohortEventAttendanceInput[] = [];
      for (const a of attendees) {
        if (a.email === null || a.selected_status === null) continue;
        attendance.push({
          email: a.email,
          status: a.selected_status,
          absent_reason: a.selected_status === 'absent' ? a.selected_reason : null,
          notes: a.notes || null,
        });
      }

      await cohortEventsApi.saveAttendance(
        cohortId,
        eventId,
        attendance,
        clearedUserIds(attendees),
      );

      notifications.show({
        title: 'Attendance Saved',
        message: `Attendance recorded for ${event?.event_name}`,
        color: 'green',
      });

      onComplete?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save attendance'));
    } finally {
      setSaving(false);
    }
  };

  // ── Notes handlers ───────────────────────────────────────────────────────

  const isContentEmpty = (html: string): boolean => {
    const stripped = html.replace(/<[^>]*>/g, '').trim();
    return stripped.length === 0;
  };

  const handleAddNote = async () => {
    if (!eventId || isContentEmpty(noteContent)) return;
    setNoteSaving(true);
    try {
      const newNote = await cohortEventsApi.createNote(cohortId, eventId, noteContent.trim());
      const updatedNotes = [newNote, ...notes];
      setNotes(updatedNotes);
      setNotesCount(updatedNotes.length);
      setNoteContent('');
      setIsEditorOpen(false);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to add note',
        color: 'red',
      });
    } finally {
      setNoteSaving(false);
    }
  };

  return (
    <>
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? '100%' : '80%'}
      padding={0}
      withCloseButton={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.sessionInfo}>
          <Group gap={8} mb={4}>
            <Badge
              size="sm"
              color="warningAmber"
              variant="light"
              styles={{ root: { textTransform: 'none', fontSize: 11 } }}
            >
              Cohort Event
            </Badge>
          </Group>
          <div className={styles.sessionTitle}>{event?.event_name ?? 'Event'}</div>
          <div className={styles.sessionDate}>
            {formatHeaderDateRange(
              event?.scheduled_start ?? null,
              event?.scheduled_end ?? null,
              event?.timezone ?? null,
            )}
          </div>
        </div>

        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Close"
        >
          <X size={18} />
        </ActionIcon>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        styles={{
          root: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          panel: { flex: 1, overflow: 'hidden' },
        }}
      >
        <Tabs.List px="md">
          <Tabs.Tab value="session">Session</Tabs.Tab>
          <Tabs.Tab
            value="notes"
            rightSection={
              notesCount > 0 ? (
                <Badge size="xs" variant="filled" color="gray" circle>
                  {notesCount}
                </Badge>
              ) : undefined
            }
          >
            Internal Notes
          </Tabs.Tab>
        </Tabs.List>

        {/* ── Session Tab ─────────────────────────────────────────────── */}
        <Tabs.Panel value="session">
          <ScrollArea style={{ height: '100%' }}>
            <Box p="md">
              <Stack gap="lg">
                {error && (
                  <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
                    <Group justify="space-between" gap="sm">
                      <Text size="sm">{error}</Text>
                      {rosterLoadFailed && (
                        <Button size="xs" variant="light" color="red" onClick={loadAttendees}>
                          Try Again
                        </Button>
                      )}
                    </Group>
                  </Alert>
                )}

                {/* Post-event attendance callout (AC14) */}
                {canMarkAttendance && !rosterLoadFailed && (
                  <Alert variant="light" color="yellow" icon={<AlertCircle size={16} />}>
                    This event has ended. Attendance has not been recorded yet.
                  </Alert>
                )}

                {/* ── Attendance (MED-1349) ─────────────────────────────── */}
                {!rosterLoadFailed && (
                  <RosterSection
                    title="Coaches"
                    rows={coachRows}
                    loading={loading}
                    editable={hasStarted}
                    showPayment
                    eventStart={event?.scheduled_start ?? null}
                    onStatus={setStatus}
                    onReason={setReason}
                    onMarkAllAttended={() => markAllAttended('coach')}
                  />
                )}

                {includesParticipants && (
                  <RosterSection
                    title="Participants"
                    rows={participantRows}
                    loading={loading}
                    editable={hasStarted}
                    showPayment={false}
                    eventStart={event?.scheduled_start ?? null}
                    onStatus={setStatus}
                    onReason={setReason}
                    onMarkAllAttended={() => markAllAttended('participant')}
                  />
                )}

                {/* ── Resources Accordion ───────────────────────────────── */}
                <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                  <UnstyledButton
                    onClick={() => setResourcesExpanded((prev) => !prev)}
                    p="sm"
                    w="100%"
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="xs">
                        <FileText size={16} color="var(--mantine-color-gray-6)" />
                        <Text size="sm" fw={600}>
                          Resources
                        </Text>
                        {resources.length > 0 && (
                          <Text size="xs" c="dimmed">
                            {resources.length} file{resources.length !== 1 ? 's' : ''}
                          </Text>
                        )}
                      </Group>
                      {resourcesExpanded ? (
                        <ChevronUp size={16} color="var(--mantine-color-gray-5)" />
                      ) : (
                        <ChevronDown size={16} color="var(--mantine-color-gray-5)" />
                      )}
                    </Group>
                  </UnstyledButton>

                  <Collapse in={resourcesExpanded}>
                    <Box px="sm" pb="sm">
                      {resourcesLoading ? (
                        <Center py="md">
                          <Loader size="sm" />
                        </Center>
                      ) : (
                        <Stack gap="xs">
                          {resources.map((resource) => (
                            <Paper
                              key={resource.id}
                              withBorder
                              p="xs"
                              radius="sm"
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                                  <File
                                    size={16}
                                    color="var(--mantine-color-gray-5)"
                                    style={{ flexShrink: 0 }}
                                  />
                                  <div style={{ minWidth: 0 }}>
                                    <Text size="sm" fw={500} truncate>
                                      {resource.title || resource.original_filename}
                                    </Text>
                                    {resource.file_size && (
                                      <Text size="xs" c="dimmed">
                                        {formatFileSize(resource.file_size)}
                                      </Text>
                                    )}
                                  </div>
                                </Group>
                                {resource.resource_type === 'file' && resource.download_url && (
                                  <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    component="a"
                                    href={resource.download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <Download size={16} />
                                  </ActionIcon>
                                )}
                              </Group>
                            </Paper>
                          ))}

                          {resources.length === 0 && (
                            <Text size="sm" c="dimmed" ta="center" py="xs">
                              No resources attached yet
                            </Text>
                          )}

                          {/* Add Resource button (dashed) */}
                          <UnstyledButton
                            p="xs"
                            style={{
                              borderRadius: 6,
                              border: '1.5px dashed var(--mantine-color-gray-4)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              color: 'var(--mantine-color-gray-6)',
                              width: '100%',
                            }}
                            onClick={() => setAddResourceModalOpened(true)}
                          >
                            <Plus size={14} />
                            <Text size="sm" fw={500}>
                              Add Resource
                            </Text>
                          </UnstyledButton>
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              </Stack>
            </Box>
          </ScrollArea>
        </Tabs.Panel>

        {/* ── Internal Notes Tab ──────────────────────────────────────── */}
        <Tabs.Panel value="notes">
          <ScrollArea style={{ height: '100%' }}>
            <Box p="md">
              <Stack gap="md">
                {/* Lock info bar */}
                <Box
                  p="sm"
                  style={{
                    backgroundColor: 'var(--mantine-color-gray-0)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Lock size={14} color="var(--mantine-color-gray-6)" />
                  <Text size="sm" c="dimmed">
                    Internal notes are only visible to Medley staff
                  </Text>
                </Box>

                {/* Add Note Editor */}
                {isEditorOpen ? (
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Add a Note
                    </Text>
                    <RichTextEditor
                      value={noteContent}
                      onChange={setNoteContent}
                      placeholder="Add internal notes about this event..."
                      minHeight={150}
                    />
                    <Group justify="flex-end">
                      <Button
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={() => {
                          setNoteContent('');
                          setIsEditorOpen(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAddNote}
                        loading={noteSaving}
                        disabled={isContentEmpty(noteContent)}
                      >
                        Save Notes
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <UnstyledButton
                    onClick={() => setIsEditorOpen(true)}
                    p="sm"
                    style={{
                      borderRadius: 8,
                      border: '2px dashed #D0CEC9',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      color: 'var(--mantine-color-gray-6)',
                      width: '100%',
                    }}
                  >
                    <Plus size={16} />
                    <Text size="sm" fw={500}>
                      Add Note
                    </Text>
                  </UnstyledButton>
                )}

                <Divider />

                {/* Notes List */}
                {notesLoading ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : (
                  <Stack gap="sm">
                    <Text size="sm" fw={500}>
                      Notes ({notes.length})
                    </Text>

                    {notes.length === 0 ? (
                      <Paper withBorder p="lg" style={{ borderStyle: 'dashed' }}>
                        <Text size="sm" c="dimmed" ta="center">
                          No notes yet. Add the first note above.
                        </Text>
                      </Paper>
                    ) : (
                      <Stack gap="sm">
                        {notes.map((note) => (
                          <Paper key={note.id} withBorder p="md">
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Group gap="xs">
                                  <Avatar size="sm" radius="xl" color="blue">
                                    {getInitials(note.author_name)}
                                  </Avatar>
                                  <div>
                                    <Text size="sm" fw={500}>
                                      {note.author_name}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {note.author_email}
                                    </Text>
                                  </div>
                                </Group>
                                <Text size="xs" c="dimmed">
                                  {formatDateTime(note.created_at)}
                                </Text>
                              </Group>
                              <RichTextContent content={note.content} />
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>
          </ScrollArea>
        </Tabs.Panel>
      </Tabs>

      {/* ── Sticky Footer ───────────────────────────────────────────────── */}
      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-2)',
          backgroundColor: '#fff',
        }}
      >
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Group gap="sm" wrap="wrap" justify="flex-end">
            {!hasStarted && (
              <Text size="sm" c="dimmed">
                Attendance can be marked only after the event starts
              </Text>
            )}
            {hasStarted && missingCount > 0 && (
              <Text size="sm" c="dimmed">
                {missingCount === 1
                  ? '1 person has no status'
                  : `${missingCount} people have no status`}
              </Text>
            )}
            {hasStarted && missingCount === 0 && reasonMissing && (
              <Text size="sm" c="dimmed">
                {reasonText}
              </Text>
            )}
            <Button
              onClick={handleSaveAttendance}
              loading={saving}
              color="medleyGreen"
              disabled={!canSave}
            >
              Save Attendance
            </Button>
          </Group>
        </Group>
      </Box>
    </Drawer>

    {/* Add Resource Modal */}
    <AddResourceModal
      opened={addResourceModalOpened}
      onClose={() => setAddResourceModalOpened(false)}
      cohortId={cohortId}
      sessions={[]}
      categories={categories}
      hideSessionSelect
      onCreated={async (resourceId) => {
        if (resourceId && eventId) {
          try {
            await cohortEventsApi.addResource(cohortId, eventId, resourceId);
          } catch {
            // Resource created but linking failed — still reload to show it
          }
        }
        loadResources();
      }}
    />
    </>
  );
}

export default CohortEventDetailDrawer;
